import React, { useState } from "react";
import { Send, Hand } from "lucide-react";

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

/** Renders chat text with any http(s) URLs turned into real clickable links,
 * opened in a new tab — everything else stays plain (safely-escaped) text. */
function Linkify({ text }) {
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("http://") || part.startsWith("https://") ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" data-testid="meeting-chat-link" className="text-cyan-400 underline hover:text-cyan-300 break-all">
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

export function MeetingChatPanel({ messages, raisedHands, isHost, selfUserId, onSend }) {
  const [text, setText] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText("");
  };

  return (
    <div className="w-full sm:w-72 flex flex-col bg-slate-900/60 border-l border-white/10 h-full" data-testid="meeting-chat-panel">
      {isHost && Object.keys(raisedHands).length > 0 && (
        <div className="p-3 border-b border-white/10 space-y-1.5" data-testid="meeting-raised-hands-list">
          <p className="text-[10px] font-bold uppercase text-amber-400 flex items-center gap-1"><Hand className="w-3 h-3" /> Raised Hands</p>
          {Object.entries(raisedHands).map(([uid, name]) => (
            <p key={uid} className="text-xs text-slate-300" data-testid={`raised-hand-${uid}`}>{name}</p>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="meeting-chat-messages">
        {messages.length === 0 && <p className="text-xs text-slate-500 text-center mt-4">No messages yet</p>}
        {messages.map((m, i) => (
          <div key={i} className={`text-xs ${m.user_id === selfUserId ? "text-cyan-300" : "text-slate-300"}`} data-testid={`meeting-chat-message-${i}`}>
            <span className="font-semibold">{m.user_id === selfUserId ? "You" : m.name}: </span>
            <span className="text-slate-200"><Linkify text={m.text} /></span>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 p-3 border-t border-white/10">
        <input
          value={text} onChange={(e) => setText(e.target.value)} placeholder="Send a message..." data-testid="meeting-chat-input"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
        />
        <button type="submit" data-testid="meeting-chat-send-btn" className="text-cyan-400 hover:text-cyan-300"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
